import { useMemo } from "react";
import { StatusBadge, useThemeSlice } from "../../components/ui/StatusBadge.jsx";
import { Ico } from "../../components/icons/icons.jsx";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { buildAccountDetailSections } from "./accountDetailFields.js";
import { buildAccountDetailTokens, detailActionBtnStyle } from "./accountDetailTheme.js";

export function AccountInfoTab({ account, onEdit, onDelete }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const sections = useMemo(() => buildAccountDetailSections(account), [account]);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          padding: "12px 16px",
          borderRadius: 10,
          background: c.hover,
          border: `1px solid ${c.cardBdr}`,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.sub, marginBottom: 3 }}>
              Company
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: c.text, lineHeight: 1.3 }}>{account.companyName}</span>
              {account.status && <StatusBadge label={account.status} accent={A} />}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button type="button" onClick={() => onEdit?.(account)} style={editBtn(c, A)} aria-label="Edit account">
            {Ico.pencil}
            Edit
          </button>
          <button
            type="button"
            onClick={() =>
              requestDelete({
                title: "Delete account",
                description: "This account and all associated data will be permanently removed.",
                confirmText: account.companyName,
                entityLabel: "Account name",
                confirmLabel: "Delete account",
                onConfirm: () => onDelete?.(account.id),
              })
            }
            style={detailActionBtnStyle(ap, "danger")}
            aria-label="Delete account"
          >
            {Ico.trash}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, alignItems: "start" }}>
        {sections.map((section) => (
          <DetailSection
            key={section.id}
            section={section}
            c={c}
            ap={ap}
            accent={A}
            style={section.span === "full" ? { gridColumn: "1 / -1" } : undefined}
          />
        ))}
      </div>

      <DeleteConfirmModal {...deleteModalProps} />
    </>
  );
}

function editBtn(c, A) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 12px",
    borderRadius: 7,
    border: `1px solid ${A}40`,
    background: `${A}0a`,
    color: A,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  };
}

function DetailSection({ section, c, ap, accent, style }) {
  const wideFields = section.fields.filter((f) => f.wide);
  const gridFields = section.fields.filter((f) => !f.wide);
  const twoCol = section.span === "full" || gridFields.length >= 2;

  return (
    <section
      style={{
        background: ap.card,
        border: `1px solid ${ap.cardBorder}`,
        borderRadius: 10,
        overflow: "hidden",
        ...style,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "9px 14px",
          borderBottom: `1px solid ${ap.cardBorder}`,
          background: c.hover,
        }}
      >
        <span style={{ width: 3, height: 14, borderRadius: 999, background: accent, flexShrink: 0 }} />
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 700, color: c.text, letterSpacing: "0.01em" }}>{section.title}</h3>
      </header>

      {gridFields.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: twoCol ? "repeat(2, minmax(0, 1fr))" : "1fr",
          }}
        >
          {gridFields.map((field, idx) => (
            <DetailRow
              key={field.label}
              field={field}
              c={c}
              ap={ap}
              accent={accent}
              showTopBorder={twoCol ? idx >= 2 : idx >= 1}
              showLeftBorder={twoCol && idx % 2 === 1}
            />
          ))}
        </div>
      )}

      {wideFields.map((field) => (
        <DetailRow key={field.label} field={field} c={c} ap={ap} accent={accent} wide showTopBorder={gridFields.length > 0} />
      ))}
    </section>
  );
}

function DetailRow({ field, c, ap, accent, wide, showTopBorder, showLeftBorder }) {
  const isWide = wide || field.wide;

  return (
    <div
      style={{
        display: isWide ? "block" : "flex",
        alignItems: "baseline",
        gap: isWide ? 0 : 12,
        padding: isWide ? "10px 14px" : "8px 14px",
        borderTop: showTopBorder ? `1px solid ${ap.cardBorder}` : undefined,
        borderLeft: showLeftBorder ? `1px solid ${ap.cardBorder}` : undefined,
        minWidth: 0,
      }}
    >
      <div
        style={{
          flex: isWide ? undefined : "0 0 108px",
          fontSize: 11,
          fontWeight: 600,
          color: c.sub,
          lineHeight: 1.35,
          marginBottom: isWide ? 4 : 0,
        }}
      >
        {field.label}
      </div>
      <FieldValue field={field} c={c} accent={accent} isWide={isWide} />
    </div>
  );
}

function FieldValue({ field, c, accent, isWide }) {
  const base = {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 600,
    color: c.text,
    lineHeight: isWide ? 1.45 : 1.35,
    wordBreak: "break-word",
  };

  if (field.type === "status") {
    return (
      <div style={base}>
        <StatusBadge label={field.status ?? field.value} accent={accent} />
      </div>
    );
  }

  if (field.type === "link" && field.value) {
    return (
      <a href={field.value} target="_blank" rel="noopener noreferrer" style={{ ...base, color: accent, textDecoration: "none" }}>
        {field.value}
      </a>
    );
  }

  if (field.type === "email" && field.value) {
    return (
      <a href={`mailto:${field.value}`} style={{ ...base, color: accent, textDecoration: "none" }}>
        {field.value}
      </a>
    );
  }

  return <div style={base}>{field.value}</div>;
}
